import type { Currency, Language } from '@/lib/interfaces/interfaces'
import type { SubscriptionPlan } from '@/lib/utils/subscriptionPlan'

// Claves i18n del tope duro de imágenes. La usan los guards cliente
// (TableBodyComponent, EditItemPopup) cuando el bar intenta subir una
// imagen teniendo ya el máximo absoluto.
export const TOO_MUCH_PREMIUM_IMGS_MSG_KEY = 'tooMuchPremiumImgs'

// Claves i18n heredadas del cap free. Se conservan porque
// `UseMenuSection` y `UseMenuTable` las siguen usando para el pre-check
// cliente de `addNewItem` / `duplicateMenuItem` / `addSection`. Cuando
// el server rechaza un save por superar el cap premium, devuelve
// `TOO_MUCH_HARD_CAP_*_KEY` (más precisas).
export const TOO_MUCH_ITEMS_KEY = 'tooMuchItems'
export const TOO_MUCH_SECTIONS_KEY = 'tooMuchSections'

// Tope duro universal (#51): cuando un bar free o premium supera
// N_PREMIUM_*, el server devuelve 403 con estas claves (sin distinguir
// plan, porque el cap es el mismo para todos).
export const TOO_MUCH_HARD_CAP_ITEMS_KEY = 'tooMuchHardCapItems'
export const TOO_MUCH_HARD_CAP_SECTIONS_KEY = 'tooMuchHardCapSections'
export const TOO_MUCH_HARD_CAP_IMGS_KEY = 'tooMuchHardCapImgs'
export const TOO_MUCH_FREE_CARTAS_KEY = 'tooMuchFreeCartas'
export const TOO_MUCH_CARTAS_KEY = 'tooMuchCartas'
export const NO_TABLE_FOUND_KEY = 'noTableFound'
export const PREM_ERROR_KEY = 'premiumFeatureError'
export const FREE_DASHBOARD_BOOKING_LIMIT_KEY = 'freeDashboardBookingLimitReached'
export const PUBLIC_BOOKINGS_UNAVAILABLE_KEY = 'publicBookingsUnavailable'

// Constantes que no requieren traducción
export const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
export const DEFAULT_THEME_FOR_NEW_BARS = 'lightbrown'
export const ADSENSE_ID = 'ca-pub-7899330733060299'
export const PUBLIC_DOMAIN = 'https://www.barmanager.app'
// Precio mensual por plan y símbolo de moneda. Es la ÚNICA fuente de verdad
// para lo que se muestra al usuario y para lo que se cobra en Stripe
// (`getCurrencyAndAmount` en `src/lib/stripe.ts` debe consumir esta misma
// constante para que display y cargo nunca diverjan). `legacy` va aparte de
// `menu` aunque hoy coincidan: cambiar Menu Pro nunca debe tocar a los legacy.
export const PLAN_PRICE_BY_SYMBOL: Record<SubscriptionPlan, Record<Currency, number>> = {
  legacy: { '€': 18, '$': 19, '£': 18 },
  menu: { '€': 18, '$': 19, '£': 18 },
  full: { '€': 30, '$': 32, '£': 30 }
}

// Multiplicador anual (10 = 2 meses gratis). Sincronizado con
// `getCurrencyAndAmount` en `src/lib/stripe.ts`.
export const YEARLY_PRICE_MULTIPLIER = 10

export const getMonthlyPrice = (plan: SubscriptionPlan, currencySymbol?: string): number =>
  PLAN_PRICE_BY_SYMBOL[plan][currencySymbol as Currency] ?? PLAN_PRICE_BY_SYMBOL[plan]['€']

export const getAnnualPerMonth = (plan: SubscriptionPlan, currencySymbol?: string): number =>
  Math.round((getMonthlyPrice(plan, currencySymbol) * YEARLY_PRICE_MULTIPLIER) / 12)

export const getAnnualTotal = (plan: SubscriptionPlan, currencySymbol?: string): number =>
  getMonthlyPrice(plan, currencySymbol) * YEARLY_PRICE_MULTIPLIER

export const isDevMode = process.env.NODE_ENV === 'development'
export const TEST_EMAIL = 'trivify@gmail.com'
export const ADMINID = isDevMode ? 'aYmQ30ZAWES3uYci1yoL7HWn75l1' : '2sBKg3xLeTXcTQrLdPsk4a1sJDI2'
export const ADMIN_EMAIL = isDevMode ? 'guybrushcon@gmail.com' : 'danidomin19@gmail.com'

// Fecha de corte del nuevo onboarding: solo los bares creados a partir de
// esta fecha tienen la propiedad `onboardingMilestones` y pueden reportar
// hitos. Los bares anteriores no entran en el funnel de onboarding y no
// deben disparar eventos. Se aplica como guard en el cliente
// (`trackOnboardingMilestoneIfNotSet`) y en el backend
// (`/api/onboarding/milestone`, `trySetOnboardingMilestone` y el funnel de
// `/api/admin/metrics`). Mantener sincronizado en todos los puntos.
export const ONBOARDING_START_DATE = new Date('2026-08-01T00:00:00.000Z')

// Slugs de bares que no deben ser indexados por Google
export const NON_INDEXABLE_SLUGS = ['pasteleria-la-gobeta']

export const URL_BASE = process.env.NODE_ENV === 'production'
  ? PUBLIC_DOMAIN
  : 'http://localhost:3000'

export const BASE_URL_QR = process.env.NODE_ENV === 'production' ? PUBLIC_DOMAIN : 'http://192.168.1.74:3000'

// Free tier limits
export const N_FREE_ITEMS = 100
export const N_FREE_IMAGES = 5
export const N_FREE_SECTIONS = 10
export const N_FREE_CARTAS = 3
export const N_FREE_DASHBOARD_BOOKINGS_MONTHLY = 30

// Paid Limits
export const N_PREMIUM_ITEMS = 800
export const N_PREMIUM_IMAGES = 200
export const N_PREMIUM_SECTIONS = 100
export const N_PREMIUM_CARTAS = 20

// Extracción de menú por IA (imagen/PDF). Se llama a través del AI Gateway de
// Vercel (API compatible con OpenAI) en lugar de usar la API de Google directa:
// un solo endpoint/facturación y observabilidad integrada. El ID del modelo va
// con prefijo de proveedor (`google/...`); se parametriza para poder cambiarlo
// sin tocar código. La API key se lee del env AI_GATEWAY_API_KEY (solo servidor).
export const AI_GATEWAY_BASE_URL = 'https://ai-gateway.vercel.sh/v1'
export const MENU_EXTRACTION_MODEL = 'google/gemini-3.1-flash-lite'
export const BLOG_IMAGE_MODEL = 'openai/gpt-image-2'

// Google Places API (New). Base URL de los endpoints REST de autocomplete y
// place details usados en el onboarding para autocompletar el nombre del bar.
// La API key se lee del env GOOGLE_PLACES_API_KEY (solo servidor, vía proxy).
export const GOOGLE_PLACES_BASE_URL = 'https://places.googleapis.com/v1'

// Cuota diaria de extracciones por bar (control anti-abuso / coste).
export const MENU_EXTRACTION_FREE_DAILY = 10
export const MENU_EXTRACTION_PREMIUM_DAILY = 30

// Límites duros del input de extracción.
export const MAX_EXTRACTION_FILE_SIZE = 10 * 1024 * 1024 // 10 MB
export const MENU_EXTRACTION_MAX_FILES = 10 // fotos/PDFs que se pueden subir en una extracción
export const MENU_EXTRACTION_MAX_PAGES = 12 // imágenes totales (fotos + páginas de PDF) enviadas al modelo por extracción
export const MAX_EXTRACTION_IMAGE_DIM = 1600 // lado mayor al que se reduce la imagen
export const MENU_EXTRACTION_IMAGE_MIME = 'image/jpeg' // formato normalizado enviado al modelo

// URLs de reseñas. Se muestran en el widget que aparece cuando el bar lleva
// ~25 días usando la app, pidiéndoles que nos valoren en Google y Trustpilot.
// El de Google es el link oficial que sale de Google Business Profile y abre
// directamente el popup de reseña (no la ficha de Maps).
export const GOOGLE_REVIEW_URL = 'https://g.page/r/CRlmmgEXF1FBEBM/review'
export const TRUSTPILOT_URL = 'https://es.trustpilot.com/review/barmanager.app'

// Edad mínima del bar (en días) antes de mostrar el widget de reseña.
// Elegido a 25 días para que el bar siga dentro del mes sin anuncios pero ya
// haya tenido tiempo real de probar el producto.
export const REVIEW_REQUEST_MIN_AGE_DAYS = 25

// Nombre por defecto de la carta de un bar recién creado, localizado por
// idioma. Solo se siembra el nombre en el idioma por defecto del bar (`name_*
// = DEFAULT_CARTA_NAME[defaultLanguage]`); el resto de idiomas se rellenará
// cuando el bar se haga premium y se ejecute la traducción. Sembrar también
// las variantes en otros idiomas aquí confundiría a
// `mergeSectionsKeepingExistingTranslations` (en
// `src/lib/azure/translateFullMenu.ts`), que trata cualquier valor no vacío
// como "ya traducido" y omitiría la traducción real.
export const DEFAULT_CARTA_NAME: Record<Language, string> = {
  es: 'Carta Principal',
  en: 'Main Menu',
  fr: 'Carte Principale',
  ca: 'Carta Principal',
  de: 'Hauptkarte',
  eu: 'Karta Nagusia'
}

// Rangos horarios (hora local del bar, formato "HH:MM") durante los que NO
// mostramos el widget de reseña: coincide con los servicios de comida y cena
// en España, cuando el dueño del bar está trabajando y no va a parar a poner
// una reseña. El modal sigue mostrándose el resto del día (mañanas, sobremesa
// tardía, noche).
export const REVIEW_PEAK_HOURS: Array<[string, string]> = [
  ['13:00', '16:00'],
  ['20:00', '23:30']
]

/**
 * @deprecated Plantilla legacy del menú de muestra.
 *
 * Ya NO se siembra en bares nuevos: desde el flujo "menú vacío" tanto
 * `POST /api/onboarding` como `POST /api/cartas` crean la carta con
 * `sections: []`. Se conserva por dos motivos:
 *   1. Detector legacy: `src/lib/utils/onboarding/activation.ts` deriva
 *      `SAMPLE_ITEM_IDS`/`SAMPLE_ORIGINAL_NAMES` de aquí para distinguir los
 *      productos de muestra intactos de los bares antiguos (no cuentan como
 *      activación real en `hasRealProduct`/`menu_published`).
 *   2. Futura plantilla opt-in ("empezar con ejemplo") sin reescribir datos.
 * No volver a usar como seed automático. Los tests de
 * `src/lib/utils/onboarding/__tests__/seedGuards.test.ts` pinan su forma
 * como detector, no como seed.
 */
export const DEFAULT_MENU = [
  {
    sectionName_es: 'Entrantes',
    sectionName_en: '',
    sectionName_fr: '',
    sectionName_ca: '',
    sectionName_de: '',
    sectionName_eu: '',
    sectionId: 'section-001',
    menuItems: [
      {
        itemId: 'item-000',
        name_es: 'Producto de muestra',
        name_en: '',
        name_fr: '',
        name_ca: '',
        name_de: '',
        name_eu: '',
        description_es: '',
        description_en: '',
        description_fr: '',
        description_ca: '',
        description_de: '',
        description_eu: '',
        price: 14.5,
        halfprice: 0,
        tapaprice: 0,
        visible: true,
        availableForDelivery: true,
        allergens: [],
        img: {
          publicId: '',
          url: ''
        }
      },
      {
        itemId: 'item-001',
        name_es: 'Producto de muestra 2',
        name_en: '',
        name_fr: '',
        name_ca: '',
        name_de: '',
        name_eu: '',
        description_es: '',
        description_en: '',
        description_fr: '',
        description_ca: '',
        description_de: '',
        description_eu: '',
        price: 19.5,
        halfprice: 0,
        tapaprice: 0,
        visible: true,
        availableForDelivery: true,
        allergens: [],
        img: {
          publicId: '',
          url: ''
        }
      }
    ]
  },
  {
    sectionName_es: 'Bebidas',
    sectionName_en: '',
    sectionName_fr: '',
    sectionName_ca: '',
    sectionName_de: '',
    sectionName_eu: '',
    sectionId: 'section-002',
    menuItems: [
      {
        itemId: 'item-002',
        name_es: 'Copa de cerveza',
        name_en: '',
        name_fr: '',
        name_ca: '',
        name_de: '',
        name_eu: '',
        description_es: '',
        description_en: '',
        description_fr: '',
        description_ca: '',
        description_de: '',
        description_eu: '',
        price: 3.5,
        halfprice: 0,
        tapaprice: 0,
        visible: true,
        availableForDelivery: true,
        img: {
          publicId: '',
          url: ''
        },
        allergens: []
      },
      {
        itemId: 'item-003',
        name_es: 'Refresco',
        name_en: '',
        name_fr: '',
        name_ca: '',
        name_de: '',
        name_eu: '',
        description_es: '',
        description_en: '',
        description_fr: '',
        description_ca: '',
        description_de: '',
        description_eu: '',
        price: 3,
        halfprice: 0,
        tapaprice: 0,
        visible: true,
        availableForDelivery: true,
        img: {
          publicId: '',
          url: ''
        },
        allergens: []
      }
    ]
  }
]
